import { Platform } from 'react-native';
import Constants from 'expo-constants';
import {
	APP_SETTINGS,
	HISTORY_RETRIEVED,
	WEATHER_RETRIEVED,
	NEWS_UPDATED,
	CHANGE_THEME,
	QUOTE_UPDATED,
	ADD_STICKY_NOTE,
	CHANGE_STICKY_NOTE,
	REMOVE_STICKY_NOTE,
	NEXT_STICKY_NOTE,
	PREVIOUS_STICKY_NOTE,
	SET_STICKY_NOTES,
	PIN_NOTE,
	NOTE_DRAGGED,
	NOTE_DROPPED,
	CLEAR_ALL_NOTES,
	UNPIN_NOTE,
	LAST_CALENDAR_SYNC_TIME,
	UPDATE_HELD_PROMO_CODE,
	MAIL_AUTHENTICATED,
	GOOGLE_LOGOUT,
	UPDATE_MAIL_COUNT,
	SET_MAIL_RETRIEVAL_ERROR,
	CLEAR_MAIL_RETRIEVAL_ERROR,
	MAIL_ITEM_SELECTED,
	CLEAR_SELECTED_MAIL,
	SET_ACTIVE_COMPANY_MAILBOX,
	CLEAR_ACTIVE_COMPANY_MAILBOX,
	UPDATE_ACTIVE_COMPANY_MAILBOX_TOKEN,
	SUCCESS,
	CSV_DATA_HELD,
	CSV_DATA_RELEASED,
	CHANGE_LANGUAGE,
	ANALYTICS_COMPONENT_CHANGED,
	GLOBAL_LOADING,
	SET_NOTIFICATION_TOKEN,
	ADDED_DATA_TO_STORE,
	SWITCH_COMPANY,
	PENDING_REFRESH,
	REFRESHED,
	MARK_NOTE_COMPLETE,
	UNMARK_NOTE,
	CLEAR_COMPANY,
	RESET_APP,
	LOGGED_IN,
	LOGGED_OUT,
	CHANGE_AVAILABILITY,
	UPDATE_LOCATION,
	CLEAR_LOCATION,
	LOCATION_ALLOWED,
	LOCATION_DENIED,
	SET_REPORT,
	CHANGE_THEME_ACCENT,
	SET_ACTIVE_ITEM,
	PAYMENT_REQUIRED,
	HIDE_PAYMENT_REQUIRED,
	SWITCH_COMPANY_REQUEST,
	SET_LEARNING_MODE,
	CLEAR_LEARNING_MODE,
	SET_WEB_SCALE,
	HIDE_MENU_INTRO_CAROUSEL,
} from '../actions/types';
import { updateObject } from './utils/globalStateFunctions';

const date = new Date().toDateString();
const accessTime = new Date().getTime();

const PROMO_CODE_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const resolveAppVersion = (): string | undefined => {
	const envVersion = process.env.REACT_APP_VERSION;
	if (envVersion && String(envVersion).trim()) return String(envVersion).trim();

	// Expo / EAS builds: prefer expoConfig.version when available.
	const expoConfigVersion =
		(Constants as any)?.expoConfig?.version ||
		(Constants as any)?.manifest?.version ||
		(Constants as any)?.manifest2?.extra?.expoClient?.version;

	if (expoConfigVersion && String(expoConfigVersion).trim()) {
		return String(expoConfigVersion).trim();
	}

	return undefined;
};

const initialState = {
	appVersion: resolveAppVersion(),
	accessDate: { date },
	accessTime: { accessTime },
	availability: 'available',
	weather: { localWeather: { temp: null }, localWeatherIcon: {} },
	location: {},
	locationAllowed: false,
	language: { value: 'English' },
	promoCode: '' as '' | { code: string; storedAt: number },
	theme: {
		lightTheme: true,
		themeColour: 'system',
		themeAccent: '',
	},
	deviceToken: '',
	news: {
		items: [],
	},
	quote: {},
	activeItem: undefined,
	stickyNotes: {
		completedNotes: [],
		activeNotes: [],
		activeNote: {},
		pinnedNotes: [],
		noteDragged: false,
	},
	socialAnalytics: {
		activeElement: 'website',
	},
	mail: {
		mailAuthenticated: false,
		mailCount: null,
		selectedMailItem: null,
		retrievalError: null,
		activeCompanyMailbox: null,
		preferPersonalMailbox: false,
	},
	data: {
		userData: {},
		companyData: {},
		dataImporter: {
			dataHeld: false,
		},
	},
	globalLoading: false,
	notificationToken: {},
	pendingRefresh: false,
	paymentRequired: false,
	paymentRequiredVisible: false,
	selectedProfile: null,
	loggedOut: true,
	learningMode: false,
	webScale: 1, // Default scale, will be set based on screen size
	menuIntroCarousel: {
		settings: false,
		data: false,
		sales: false,
		projects: false,
		networking: false,
		knowledge: false,
		company: false,
	},
	lastCalendarSyncTime: null,
};
//TODO Bug on user change, history is retrieved from previous user logged in
export default function (state = initialState, action) {
	switch (action.type) {
		case LAST_CALENDAR_SYNC_TIME:
			return updateObject(state, {
				lastCalendarSyncTime: action.payload,
			});
		case UPDATE_HELD_PROMO_CODE: {
			const code = action.payload;
			if (!code || typeof code !== 'string' || !code.trim()) {
				return updateObject(state, { promoCode: '' });
			}
			return updateObject(state, {
				promoCode: { code: code.trim(), storedAt: Date.now() },
			});
		}
		case 'UPDATE_DEVICE_TOKEN':
			return updateObject(state, {
				deviceToken: action.payload,
			});
		case CHANGE_THEME_ACCENT:
			return updateObject(state, {
				themeAccent: action.payload.themeColour,
			});
		case HIDE_PAYMENT_REQUIRED:
			return updateObject(state, {
				paymentRequiredVisible: false,
			});
		case SET_ACTIVE_ITEM:
			return updateObject(state, {
				activeItem: action.payload,
			});
		case CLEAR_LOCATION:
			return updateObject(state, {
				location: {},
			});
		case LOCATION_ALLOWED:
			return updateObject(state, {
				locationAllowed: true,
			});
		case LOCATION_DENIED:
			return updateObject(state, {
				locationAllowed: false,
			});
		case UPDATE_LOCATION:
			return updateObject(state, {
				location: action.payload,
			});
		case RESET_APP:
			return updateObject(initialState);
		case SUCCESS:
			return updateObject(state, { loggedOut: false });
		case LOGGED_OUT:
			return updateObject(initialState, {
				loggedOut: true,
				menuIntroCarousel:
					state.menuIntroCarousel ?? initialState.menuIntroCarousel,
			});
		case SET_LEARNING_MODE:
			return updateObject(state, { learningMode: true });
		case CLEAR_LEARNING_MODE:
			return updateObject(state, { learningMode: false });
		case SET_WEB_SCALE:
			return updateObject(state, { webScale: action.payload });
		case HIDE_MENU_INTRO_CAROUSEL: {
			const key = action.payload;
			if (
				key !== 'settings' &&
				key !== 'data' &&
				key !== 'sales' &&
				key !== 'projects' &&
				key !== 'networking' &&
				key !== 'knowledge' &&
				key !== 'company'
			) {
				return state;
			}
			const prev = state.menuIntroCarousel ?? {
				settings: false,
				data: false,
				sales: false,
				projects: false,
				networking: false,
				knowledge: false,
				company: false,
			};
			return updateObject(state, {
				menuIntroCarousel: { ...prev, [key]: true },
			});
		}
		case GLOBAL_LOADING:
			return updateObject(state, {
				globalLoading: action.payload,
			});
		// TODO CHECK THIS ONE
		case SWITCH_COMPANY_REQUEST:
			return updateObject(state, {
				selectedProfile: action.payload,
				mail: {
					...state.mail,
					activeCompanyMailbox: null,
					preferPersonalMailbox: false,
					retrievalError: null,
				},
			});
		case CLEAR_COMPANY:
			return updateObject(state, {
				selectedProfile: null,
				mail: {
					...state.mail,
					activeCompanyMailbox: null,
					preferPersonalMailbox: false,
				},
			});
		case SET_ACTIVE_COMPANY_MAILBOX:
			return updateObject(state, {
				mail: {
					...state.mail,
					activeCompanyMailbox: action.payload || null,
					preferPersonalMailbox: false,
					retrievalError: null,
				},
			});
		case CLEAR_ACTIVE_COMPANY_MAILBOX:
			return updateObject(state, {
				mail: {
					...state.mail,
					activeCompanyMailbox: null,
					preferPersonalMailbox: action.payload?.preferPersonal === true,
				},
			});
		case UPDATE_ACTIVE_COMPANY_MAILBOX_TOKEN: {
			const current = state.mail?.activeCompanyMailbox;
			const companyId = action.payload?.companyId;
			const email = action.payload?.email;
			const matchesCurrent =
				current &&
				(!companyId || String(current.companyId) === String(companyId)) &&
				(!email || String(current.email || '').toLowerCase() === String(email).toLowerCase());
			// Never invent a company mailbox from a token refresh when none is active.
			const nextMailbox = matchesCurrent
				? {
						...current,
						accessToken: action.payload?.accessToken || action.payload?.newToken || current.accessToken,
						googleId: action.payload?.googleId || current.googleId,
						scopes: action.payload?.scopes || action.payload?.newScopes || current.scopes,
						email: email || current.email,
						companyId: companyId ? String(companyId) : current.companyId,
					}
				: current;
			return updateObject(state, {
				mail: {
					...state.mail,
					activeCompanyMailbox: nextMailbox || null,
				},
			});
		}
		case PENDING_REFRESH:
			return updateObject(state, {
				pendingRefresh: true,
			});
		case CHANGE_AVAILABILITY:
			return updateObject(state, {
				...state,
				availability: action.payload,
			});
		case REFRESHED:
			return updateObject(state, {
				pendingRefresh: false,
			});
		case ADDED_DATA_TO_STORE:
			return updateObject(state, {
				...state,
			});
		case MAIL_AUTHENTICATED:
			return updateObject(state, {
				mail: {
					...state.mail,
					mailAuthenticated: true,
					retrievalError: null,
				},
			});
		case SET_NOTIFICATION_TOKEN:
			return updateObject(state, {
				notificationToken: action.payload,
			});
		case ANALYTICS_COMPONENT_CHANGED:
			return updateObject(state, {
				socialAnalytics: {
					activeElement: action.payload,
				},
			});
		case CHANGE_LANGUAGE:
			return updateObject(state, {
				language: { value: action.payload },
			});
		case CSV_DATA_HELD:
			return updateObject(state, {
				data: {
					dataImporter: {
						dataHeld: true,
					},
				},
			});
		case CSV_DATA_RELEASED:
			return updateObject(state, {
				data: {
					dataImporter: {
						dataHeld: false,
					},
				},
			});
		case UPDATE_MAIL_COUNT:
			return updateObject(state, {
				mail: {
					...state.mail,
					mailCount: action.payload,
					retrievalError: null,
				},
			});
		case SET_MAIL_RETRIEVAL_ERROR:
			return updateObject(state, {
				mail: { ...state.mail, retrievalError: action.payload },
			});
		case CLEAR_MAIL_RETRIEVAL_ERROR:
			return updateObject(state, {
				mail: { ...state.mail, retrievalError: null },
			});
		case MAIL_ITEM_SELECTED:
			return updateObject(state, {
				mail: {
					...state.mail,
					selectedMailItem: action.payload,
				},
			});
		case CLEAR_SELECTED_MAIL:
			return updateObject(state, {
				mail: { ...state.mail, selectedMailItem: null },
			});
		case SUCCESS:
			return updateObject(state, {
				mail: { ...state.mail, selectedMailItem: null },
			});
		case GOOGLE_LOGOUT:
			return updateObject(state, {
				mail: {
					...state.mail,
					mailAuthenticated: false,
					activeCompanyMailbox: null,
				},
			});
		case ADD_STICKY_NOTE:

			const newActiveNotes = [
				...state.stickyNotes.activeNotes,
				action.payload,
			];
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					activeNotes: newActiveNotes,
					activeNote: action.payload,
				},
			});
		case SET_STICKY_NOTES:

			let activeNoteList = [];
			let completedNoteList = [];
			action.payload.map((notes) => {
				if (notes.completed) {
					completedNoteList.push(notes);
				} else activeNoteList.push(notes);
			});
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					activeNotes: activeNoteList,
					completedNotes: completedNoteList,
				},
			});
		case CLEAR_ALL_NOTES:
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					pinnedNotes: [],
				},
			});
		case CHANGE_STICKY_NOTE:
			const updatedActiveNotes = state.stickyNotes.activeNotes.map(
				(note) =>
					note._id === action.payload._id
						? { ...note, ...action.payload }
						: note
			);
			const updatedCompletedNotes = state.stickyNotes.completedNotes.map(
				(note) =>
					note._id === action.payload._id
						? { ...note, ...action.payload }
						: note
			);
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					activeNotes: updatedActiveNotes,
					completedNotes: updatedCompletedNotes,
					activeNote: action.payload,
				},
			});
		case PREVIOUS_STICKY_NOTE:
			const findPrevNote = () => {
				if (action.payload._id) {
					return state.stickyNotes.activeNotes.filter(
						(id) => id === action.payload._id
					);
				} else return 0;
			};
			return updateObject(state, {
				...state,
			});
		case REMOVE_STICKY_NOTE:
			const newNotes = state.stickyNotes.activeNotes.filter(
				(note) => note._id !== action.payload
			);
			const newCompletedNotes = state.stickyNotes.completedNotes.filter(
				(note) => note._id !== action.payload
			);
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					activeNotes: newNotes,
					completedNotes: newCompletedNotes,
					activeNote: {},
				},
			});
		case PIN_NOTE: {
			const pinId = action.payload._id ?? action.payload.id;
			const newPinnedNote = {
				id: pinId,
				_id: pinId,
				content: action.payload.content,
				color: action.payload.color,
				...(action.payload.source ? { source: action.payload.source } : {}),
			};
			const alreadyPinned =
				pinId != null &&
				state.stickyNotes.pinnedNotes.some((n) => (n._id ?? n.id) === pinId);
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					noteDragged: false,
					pinnedNotes: alreadyPinned
						? state.stickyNotes.pinnedNotes
						: [...state.stickyNotes.pinnedNotes, newPinnedNote],
				},
			});
		}
		case UNPIN_NOTE:
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					pinnedNotes: state.stickyNotes.pinnedNotes.filter(
						(n) => (n._id ?? n.id) !== action.payload
					),
				},
			});
		case MARK_NOTE_COMPLETE:
			const amendedNotes = state.stickyNotes.activeNotes.filter(
				(note) => note._id !== action.payload._id
			);
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					activeNotes: amendedNotes,
					completedNotes: [
						...state.stickyNotes.completedNotes,
						action.payload,
					],
				},
			});
		case UNMARK_NOTE:
			const appendedNotes = state.stickyNotes.completedNotes.filter(
				(note) => note._id != action.payload._id
			);

			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					activeNotes: [...appendedNotes, action.payload],
					completedNotes: appendedNotes,
				},
			});
		case NOTE_DRAGGED:
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					noteDragged: true,
				},
			});
		case NOTE_DROPPED:
			return updateObject(state, {
				stickyNotes: {
					...state.stickyNotes,
					noteDragged: false,
				},
			});
		case HISTORY_RETRIEVED:
			return updateObject(state, {
				queryHistory: action.payload,
			});
		case WEATHER_RETRIEVED:
			return updateObject(state, {
				weather: {
					localWeather: action.payload.main,
					localWeatherIcon: action.payload.weather[0],
				},
			});
		case NEWS_UPDATED:
			return updateObject(state, {
				news: {
					items: action.payload,
				},
			});
		case QUOTE_UPDATED:
			return updateObject(state, {
				quote: action.payload,
			});
		case CHANGE_THEME:
			return updateObject(state, {
				theme: {
					lightTheme: action.payload.lightTheme,
					themeColour: action.payload.theme ?? action.payload.themeColour,
				},
			});
		default:
			return state;
	}
}
